<template>
  <ToggleSection
    v-model="showBotData"
    :title="t('course_settings.advanced_settings_texts.add_bot')"
    :description="t('course_settings.advanced_settings_texts.add_bot_text')"
    :show-content="showBotData">
    <div v-if="showBotData" class="flex flex-col items-center gap-2 mt-3">
      <div
        v-for="bot in fetchedBots"
        :key="bot.name"
        class="w-full flex flex-col items-center">
        <div
          v-if="bot.isTemporary"
          class="flex flex-col gap-2 items-start w-full">
          <span class="text-xs text-secondary-900">
            {{ t('course_settings.advanced_settings_texts.bot_id') }}
          </span>
          <div
            class="flex items-center w-full gap-3 relative"
            :class="{
              'text-red-600': bot.status === 'error',
              'mb-2': bot.errorMessage,
            }">
            <CircleMinus
              v-if="!bot.status"
              :size="16"
              class="cursor-pointer"
              @click="removeBot(bot.id)"/>
            <BaseInput
              v-model="bot.id"
              :error="bot.status === 'error'"
              @keydown.enter="addBot(bot)">
              <template #suffix>
                <ProgressSpinner
                  v-if="bot.loading"
                  :style="{ height: '2rem', width: '2rem' }"/>
                <button
                  v-else-if="bot.status === 'error'"
                  @click="removeBot(bot.id)">
                  <CircleX :size="16" />
                </button>
                <CircleArrowLeft
                  v-else
                  :class="{
                    'stroke-secondary-450 cursor-not-allowed': !bot.id,
                    'stroke-secondary-900 cursor-pointer': bot.id,
                  }"
                  :size="16"
                  @click="addBot(bot)"/>
              </template>
            </BaseInput>
            <div
              v-if="bot.errorMessage"
              class="absolute right-[1rem] bottom-[-1rem] text-xs text-red-600">
              {{ bot.errorMessage }}
            </div>
          </div>
        </div>
        <div v-else class="w-full flex flex-col gap-3">
          <div class="flex flex-col items-start gap-2">
            <span class="text-xs font-normal">
              {{ t('course_settings.advanced_settings_texts.bot_id') }}
            </span>
            <div
              class="flex items-center justify-between px-4 py-[0.625rem] w-full border-1 border-solid border-secondary-300 text-secondary-800 rounded-sm">
              <span
                class="font-normal text-base"
                :class="{
                  'text-green-600': bot.status === 'success',
                  'text-red-600': bot.status === 'error',
                  'text-secondary-800': !bot.status,
                }">
                {{ bot.id }}
              </span>
              <ProgressSpinner
                v-if="bot.loading"
                :style="{ height: '2rem', width: '2rem' }"/>
              <button v-else @click="removeBot(bot.id)">
                <CircleX :size="16" />
              </button>
            </div>
          </div>
          <div class="flex items-center w-full">
            <div class="flex flex-col items-start w-full gap-2">
              <span class="text-xs font-normal">
                {{ t('course_settings.advanced_settings_texts.bot_name') }}
              </span>
              <div
                class="w-full rounded-sm flex items-center justify-between border-1 order-solid border-secondary-300 px-4 py-[0.625rem]">
                <span class="font-normal text-base text-secondary-800">
                  {{ bot.name }}
                </span>
                <img class="h-6 w-6 rounded-full" :src="bot.image" alt="bot" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- <div v-if="fetchedBots.length < 6"
        class="cursor-pointer flex items-center justify-start gap-2 w-full py-3 px-4 border-1 border-solid border-secondary-300 rounded-md"
        @click="addEmptyBot">
        <div class="h-8 w-8 bg-primary-200 rounded-full flex items-center justify-center">
          <Plus :size="12" />
        </div>
        <p class="text-secondary-900 text-sm font-semibold">
          {{ t('course_settings.advanced_settings_texts.add_another_bot') }}
        </p>
      </div> -->

      <!-- Bot Info Button -->
      <div class="w-full mt-2 flex justify-end">
        <BaseButton
          class="h-[34px] !rounded-[4px]"
          :disabled="fetchedBots.length > 5"
          @click="openBotModal">
          גישה למחולל העמיתים
        </BaseButton>
      </div>

      <!-- Bot Info Dialog -->
      <PVDialog
        v-model:visible="showBotInfoDialog"
        :modal="true"
        :closable="true"
        :dismissable-mask="true"
        :style="{ width: '100vw', height: '100vh' }"
        :pt="{
          mask: {
            class: ['!z-[1301]'],
          },
          content: {
            style: { width: '100%', height: '100%' },
          },
        }">
        <template #header>
          <div class="text-secondary-900 font-semibold">ספריית עמיתים</div>
        </template>
        <div class="flex flex-col gap-4 h-full">
          <div v-if="iframeUrl" class="w-full h-full">
            <!-- <BaseIframe
                :src="iframeUrl"
                :height="'100%'"
                :width="'100%'"
                type="website"
                :title="'Bot Generator'"
                :is-preview="false" /> -->

            <iframe
              ref="iframeRef"
              :src="iframeUrl"
              :height="'100%'"
              :width="'100%'"
              :title="'Bot Generator'"
              allow="microphone"
              style="border: none"
              @load="onIframeLoad"/>
          </div>
          <div v-else class="flex items-center justify-center h-[200px]">
            <ProgressSpinner :style="{ height: '2rem', width: '2rem' }" />
          </div>
        </div>
      </PVDialog>
    </div>
  </ToggleSection>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseInput from '@/components/Input/BaseInput.vue';
import { useVModel } from '@vueuse/core';
import { ToggleSection } from '@amit/components';
import { CircleArrowLeft, CircleX, CircleMinus } from 'lucide-vue-next';
import ProgressSpinner from 'primevue/progressspinner';
import PVDialog from 'primevue/dialog';
import { IBot } from 'packages/components/types/interfaces';
import BaseButton from '@/components/Button/BaseButton.vue';
import { useFirebase } from '@amit/auth';

const { t } = useI18n();

const props = defineProps({
  addons: {
    type: Array as () => IBot[],
    required: true,
  },
  isLesson: {
    type: Boolean,
    required: true,
  },
  userId: {
    type: String,
    required: true,
  },
  courseId: {
    type: String,
    default: '',
  },
  lessonId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:modelValue',
  'update:hasErrors',
  'update:showBotData',
  'openBotGenerator',
]);

const addons = useVModel(props, 'addons', emit);

const OLD_BOTS = [
  '65f0ad6b843d44042bae85de',
  '65dcf926d91456735928727b',
  '65f4e0b248e85e8e07211782',
  '65f4e41a48e85e8e07211783',
];

const OLD_SERVER_URL = 'https://amit-bots.com';
const BOT_SERVER_URL =
  import.meta.env.VITE_BOT_GENERATOR_URL ||
  'https://botgen-prod-1031090991817.me-west1.run.app/';

const showBotData = ref(false);
const showBotInfoDialog = ref(false);
const iframeUrl = ref<string>('');
const iframeRef = ref();

interface BotItem {
  id: string;
  name: string;
  supportedResponseTypes: string[];
  image: string;
  idleVideo: string;
  isTemporary: boolean;
  status: string;
  loading: boolean;
  errorMessage?: string;
}

const fetchedBots = ref<BotItem[]>([]);

const botTemplate = {
  settings: {
    recognition_lang: 'en-us',
    backgroundColor: '#575754',
    botId: '',
    level: 'A1',
  },
  type: 'assistant',
};

const fetchBot = async (botId: string) => {
  try {
    const url = OLD_BOTS.includes(botId) ? OLD_SERVER_URL : BOT_SERVER_URL;
    const response = await fetch(`${url}/bots/${botId}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return { ...data, id: botId };
  } catch (error) {
    console.error('Error fetching bot:', error);
    throw error;
  }
};

const initializeFetchedBots = async () => {
  fetchedBots.value = [];
  const addonsSnapshot = [...addons.value];

  // Create an array of promises for all bot fetches
  const botPromises = addonsSnapshot.map(async addon => {
    try {
      const bot = await fetchBot(addon.settings.botId);
      return { success: true, bot, botId: addon.settings.botId };
    } catch (error) {
      console.error(`Error fetching bot ${addon.settings.botId}:`, error);
      return { success: false, error, botId: addon.settings.botId };
    }
  });

  // Wait for all promises to settle (either resolve or reject)
  const results = await Promise.allSettled(botPromises);

  // Process successful results
  results.forEach(result => {
    if (
      result.status === 'fulfilled' &&
      result.value.success &&
      result.value.bot
    ) {
      const bot = result.value.bot;
      bot.isTemporary = false;
      fetchedBots.value.push({
        id: result.value.botId,
        ...bot,
        loading: false,
        status: '',
      });
    }
  });
};

const addEmptyBot = () => {
  fetchedBots.value.push({
    id: '',
    name: '',
    supportedResponseTypes: [],
    image: '',
    idleVideo: '',
    isTemporary: true,
    status: '',
    loading: false,
  });
};

const addBot = async (botItem: BotItem) => {
  hasErrors.value = false;

  const botId = botItem.id;

  botItem.loading = true;
  const addonCount = fetchedBots.value.filter(bot => bot.id === botId).length;
  if (addonCount > 1) {
    botItem.loading = false;
    botItem.status = 'error';

    botItem.id = 'id-error';
    fetchedBots.value = fetchedBots.value.filter(bot => bot.id !== 'id-error');

    botItem.errorMessage = t(
      'course_settings.advanced_settings_texts.duplicate_bot_error',
    );

    return;
  }
  checkForErrors();
  try {
    const bot = await fetchBot(botId);
    if (bot) {
      addons.value.push({
        ...botTemplate,
        settings: { ...botTemplate.settings, botId },
      });
      bot.loading = false;
      bot.status = 'success';
      const botIndex = fetchedBots.value.findIndex(
        fetchedBot => fetchedBot.id === botId,
      );
      if (botIndex !== -1) {
        fetchedBots.value.splice(botIndex, 1, {
          ...bot,
          isTemporary: false,
        });
      }
    } else {
      checkForErrors();
    }
  } catch (error) {
    console.error('Error adding bot:', error);
    botItem.loading = false;
    botItem.status = 'error';
    botItem.errorMessage = t(
      'course_settings.advanced_settings_texts.fetch_bot_error',
    );
  }
};

const removeBot = (botId: string) => {
  const addonIndex = addons.value.findIndex(
    addon => addon.settings.botId === botId,
  );
  if (addonIndex !== -1) {
    addons.value.splice(addonIndex, 1);
  }

  const botIndex = fetchedBots.value.findIndex(bot => bot.id === botId);
  if (botIndex !== -1) {
    fetchedBots.value.splice(botIndex, 1);
  }
  checkForErrors();
};

const hasErrors = ref(false);

const checkForErrors = () => {
  hasErrors.value = fetchedBots.value.some(bot => bot.status === 'error');
  emit('update:hasErrors', hasErrors.value);
};

const openBotModal = async () => {
  try {
    // Use the real userId from props instead of hardcoded value

    // Get the current user's token for authorization
    const auth = useFirebase();
    const currentUserToken = await auth.currentUser?.getIdToken();

    if (!currentUserToken) {
      throw new Error('No authentication token available');
    }

    const res = await fetch(
      `${import.meta.env.VITE_API_BASE}/auth/impersonate`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentUserToken}`,
        },
        body: JSON.stringify({ user: props.userId }),
      },
    );

    if (!res.ok) {
      throw new Error(`HTTP error! status: ${res.status}`);
    }

    const response = await res.json();
    const { token } = response.data;

    // Build URL with token, courseId, and lessonId parameters
    const urlParams = new URLSearchParams();
    urlParams.append('token', token);

    if (props.courseId) {
      urlParams.append('courseId', props.courseId);
    }

    if (props.lessonId) {
      urlParams.append('lessonId', props.lessonId);
    }

    iframeUrl.value = `${import.meta.env.VITE_BOT_GENERATOR_URL}/?${urlParams.toString()}`;
    console.log('iframeUrl', iframeUrl.value);
    showBotInfoDialog.value = true;
  } catch (error) {
    console.error('Error fetching Firebase token:', error);
    // Handle error - maybe show a toast or error message
  }
};

const handleMessage = (event: MessageEvent) => {
  // Handle test message
  if (event.data?.type === 'test') {
    return;
  }

  // Check for the correct origin (the bot generator URL from environment)
  const botGeneratorOrigin = new URL(import.meta.env.VITE_BOT_GENERATOR_URL)
    .origin;
  if (event.origin !== botGeneratorOrigin) {
    return;
  }

  if (event.data?.type.toLowerCase() === 'bot_selected') {
    const botId = event.data.payload.botId;

    // Add the selected bot to the list
    addEmptyBot();
    const lastBot = fetchedBots.value[fetchedBots.value.length - 1];
    if (lastBot && lastBot.isTemporary) {
      lastBot.id = botId;
      addBot(lastBot);
    }
    showBotInfoDialog.value = false;
  }
};

const onIframeLoad = () => {
  console.log('Iframe loaded successfully');
  console.log('Iframe URL:', iframeUrl.value);

  // Test if the message listener is working
  setTimeout(() => {
    console.log('Sending test message to verify listener is working');
    window.postMessage({ type: 'test', message: 'Test message' }, '*');
  }, 1000);
};

onMounted(() => {
  console.log('BotSettings component mounted');
  initializeFetchedBots();
  if (addons.value.length > 0) {
    showBotData.value = true;
  }
  window.addEventListener('message', handleMessage);
  console.log('Message event listener added');
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMessage);
});

watch(
  () => addons.value,
  () => {
    if (!props.isLesson) {
      initializeFetchedBots();
    }
    if (addons.value.length > 0) {
      showBotData.value = true;
    }
  },
);

watch(showBotData, () => {
  emit('update:showBotData', showBotData.value);
});
</script>
